import type { MouseEvent, ReactNode } from "react";

import { ExternalLinkIcon } from "../../Icons";
import { Typo } from "../../Typo";

export type ReportLinkCellProps = {
  primary: ReactNode;
  supportive?: ReactNode;
  href: string | undefined;
  accessibleName: string;
  onNavigate?: (href: string) => void;
  "data-testid"?: string;
};

const isModifiedClick = (event: MouseEvent): boolean =>
  event.button !== 0 ||
  event.defaultPrevented ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

export const ReportLinkCell = ({
  primary,
  supportive,
  href,
  accessibleName,
  onNavigate,
  "data-testid": testId,
}: ReportLinkCellProps) => {
  const PrimaryText = (
    <Typo element="span" type="bodyMD">
      {primary}
    </Typo>
  );
  const SupportiveText = supportive ? (
    <Typo element="span" type="bodyXS" textColor="text-text-secondary">
      {supportive}
    </Typo>
  ) : null;

  if (!href) {
    return (
      <div className="flex flex-col gap-0.5" data-testid={testId}>
        <div className="inline-flex items-center gap-2">{PrimaryText}</div>
        {SupportiveText}
      </div>
    );
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!onNavigate || isModifiedClick(event)) return;
    event.preventDefault();
    onNavigate(href);
  };

  return (
    <a
      href={href}
      aria-label={accessibleName}
      onClick={handleClick}
      data-testid={testId}
      // No `hover:bg-*`: the `TableDesktop` row already paints the same tint, so a
      // cell-level one would stack. `focus-visible` keeps a tint for keyboard users.
      className="group -m-3 flex flex-col gap-0.5 p-3 outline-none focus-visible:bg-state-selected focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stroke-focused"
    >
      <div className="inline-flex items-center gap-2">
        <Typo
          element="span"
          type="bodyMD"
          className="group-hover:underline group-focus-visible:underline"
        >
          {primary}
        </Typo>
        <ExternalLinkIcon
          aria-hidden="true"
          focusable="false"
          className="hidden h-4 w-4 shrink-0 group-hover:inline-block group-focus-visible:inline-block [&_path]:stroke-icon-selected"
        />
      </div>
      {supportive != null && (
        <Typo
          element="span"
          type="bodyXS"
          textColor="text-text-secondary"
          className="group-hover:underline group-focus-visible:underline"
        >
          {supportive}
        </Typo>
      )}
    </a>
  );
};
